import { zodResolver } from "@hookform/resolvers/zod";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
    Divider,
} from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { AllDayTodoSwitches } from "../../components/form/AllDayTodoSwitches";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
import { useCreateContactMutation } from "../../services/contactsService";
import {
    useCreateTaskMutation,
    useFinalizeTaskMutation,
    useGetTaskStatuses,
    useGetTaskSubjects,
    useGetTaskSubsubjects,
} from "../../services/taskService";
import { useGetSellers } from "../../services/usersApi";
import { generateOutlookCalendarLink } from "../../utils";
import { ProductSelector } from "../LeadsPage/ProductSelector";
import { getDefaultTaskPeriodStr } from "./utils";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import CopyToClipboardButton from "../../components/buttons/CopyToClipBoardButton";
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import LeadsContactsOffersAgrementsTabs from "../PersonRepositoryPage/LeadsContactsOffersAgrementsTabs";
import EditIcon from "@mui/icons-material/Edit";
import { EditTaskDialog } from "./EditTaskDialog";
import { getFieldsFromObject, formatPhoneNumer } from "../../helpers/methods";

const CURRENT_TASK_FIELDS_TO_EDIT = [
    "name",
    "way",
    "note"
]

const finalizeTaskFormSchema = z
    .object({
        name: z.string().min(1, { message: t("tasks.validation.nameRequired") }),
        way: z.enum(["PHONE", "EMAIL", "MEETING"]),
        priority: z.enum(["LOW", "NORMAL", "HIGH"]).default("NORMAL"),
        date: z.string(),
        startTime: z.string().optional(),
        stopTime: z.string().optional(),
        note: z.string().min(1, { message: t("tasks.validation.noteRequired") }),
        subject: z.string().min(1, { message: t("tasks.validation.subjectRequired") }),
        subsubject: z.string().optional(),
        seller: z.string().min(1, { message: t("tasks.validation.sellerRequired") }),
        status: z.string().optional(),
        contactNote: z.string().optional(),
        productIds: z.array(z.string()).default([]),
    })
    .refine(
        (data) => {
            if (data.startTime && data.stopTime) {
                return data.stopTime > data.startTime;
            }
            return true;
        },
        {
            message: t("tasks.validation.stopAfterStart"),
            path: ["stopTime"],
        },
    );

export function FinalizeTaskDialog({ task, onClose }) {
    const [taskFormData, setTaskFormData] = useState(task)
    const [wasCompleted, setWasCompleted] = useState(false);
    const [endService, setEndService] = useState(false);
    const [addToCalendar, setAddToCalendar] = useState(false);
    const [isNameValueSet, setIsNameValueSet] = useState(false);
    const [isAllDay, setIsAllDay] = useState(false);
    const [isTodo, setIsTodo] = useState(false);

    dayjs.locale("pl");

    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0];
    const defaultPeriod = getDefaultTaskPeriodStr(new Date(), task.way);

    const createFinalizeTaskFormSchema = (endService, wasCompleted) =>
        z
            .object({
                name: endService
                    ? z.string().optional()
                    : z.string().min(1, { message: t("tasks.validation.nameRequired") }),
                way: endService
                    ? z.enum(["PHONE", "EMAIL", "MEETING"]).optional()
                    : z.enum(["PHONE", "EMAIL", "MEETING"]),
                priority: z.enum(["LOW", "NORMAL", "HIGH"]).default("NORMAL"),
                date: z.string(),
                startTime: z.string().optional(),
                stopTime: z.string().optional(),
                note: endService
                    ? z.string().optional()
                    : z.string().optional(),
                subject: endService
                    ? z.string().optional()
                    : z.string().min(1, { message: t("tasks.validation.subjectRequired") }),
                subsubject: z.string().optional(),
                seller: endService
                    ? z.string().optional()
                    : z.string().min(1, { message: t("tasks.validation.sellerRequired") }),
                status: z.string().optional(),
                contactNote: wasCompleted
                    ? z.string().min(1, { message: t("tasks.validation.contactNoteRequired") })
                    : z.string().optional(),
                productIds: z.array(z.string()).default([]),
            })
            .refine(
                (data) => {
                    if (!endService && data.startTime && data.stopTime) {
                        return data.stopTime > data.startTime;
                    }
                    return true;
                },
                {
                    message: t("tasks.validation.stopAfterStart"),
                    path: ["stopTime"],
                },
            );

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        clearErrors,
    } = useForm({
        resolver: zodResolver(createFinalizeTaskFormSchema(endService, wasCompleted)),
        defaultValues: {
            status: "",
            way: task.way,
            priority: task.priority,
            date: formattedDate,
            seller: task.seller.id,
            contactNote: "",
            productIds: [],
            startTime: defaultPeriod.start,
            stopTime: defaultPeriod.end,
        },
    });

    useEffect(() => {
        clearErrors();
    }, [endService, wasCompleted, clearErrors]);

    const { data: statuses } = useGetTaskStatuses({
        select: (data) =>
            data.results.map((status) => ({
                label: t(`tasks.statuses.${status.status_name_eng}`),
                value: status.id,
            })),
    });

    useEffect(() => {
        if (statuses && statuses.length > 1 && !watch("status")) {
            setValue("status", statuses[1].value);
        }
    }, [statuses, setValue, watch]);

    const selectedSubject = watch("subject");
    const { data: subjects } = useGetTaskSubjects({
        select: (data) =>
            data.results.map((subject) => ({
                label: t(`tasks.subjects.${subject.name_eng}`),
                value: subject.id,
            })),
    });

    const { data: subsubjects } = useGetTaskSubsubjects(selectedSubject, {
        select: (data) =>
            data.map((subsubject) => ({
                label: t(`tasks.subsubjects.${subsubject.name_eng}`),
                value: subsubject.id,
            })),
        enabled: !!selectedSubject,
    });

    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                label: seller.full_name,
                value: seller.id,
            })),
    });

    const { mutate: createContact } = useCreateContactMutation({
        onSuccess: () => {
            toast.success(t("contacts.alerts.createSuccess"));
        },
        onError: (error) => {
            console.error(error);
            toast.custom(() => <SnackbarError message={t("contacts.createError")} />);
        },
    });

    const { mutate: finalizeTask } = useFinalizeTaskMutation({
        onSuccess: () => {},
        onError: (error) => {
            console.error(error);
            toast.custom(() => <SnackbarError message={t("tasks.finalizeError")} />);
        },
    });

    const { mutate: createTask } = useCreateTaskMutation({
        onSuccess: () => {
            toast.success(t("tasks.alerts.finalizeSuccess"));
            onClose();
        },
        onError: (error) => {
            console.error(error);
            toast.custom(() => <SnackbarError message={t("tasks.createError")} />);
        },
    });

    const onSubmit = (data) => {
        let startDate = null;
        let stopDate = null;

        if (!isTodo && data.date) {
            if (isAllDay) {
                startDate = dayjs(data.date).startOf("day").toDate().toISOString();
                stopDate = dayjs(data.date).endOf("day").toDate().toISOString();
            } else if (data.startTime && data.stopTime) {
                const startDateTime = dayjs(`${data.date}T${data.startTime}`);
                const stopDateTime = dayjs(`${data.date}T${data.stopTime}`);
                startDate = startDateTime.toDate().toISOString();
                stopDate = stopDateTime.toDate().toISOString();
            }
        }

        const newTaskData = !endService
            ? {
                  name: data.name,
                  way: data.way,
                  priority: data.priority,
                  start: startDate,
                  stop: stopDate,
                  note: data.note,
                  subject: data.subject,
                  subsubject: data.subsubject || undefined,
                  seller: data.seller,
                  status: data.status,
                  is_allday: isAllDay,
                  customer: task.customer?.id,
              }
            : undefined;

        finalizeTask(
            {
                taskId: task.id,
                createContact: wasCompleted,
                contactNote: wasCompleted ? data.contactNote : undefined,
                createNewTask: !endService,
                newTaskData,
            },
            {
                onSuccess: () => {
                    if (wasCompleted && task.customer?.person) {
                        createContact(
                            {
                                way: task.way.charAt(0).toUpperCase() + task.way.slice(1).toLowerCase(),
                                subject: task.subject.name_eng,
                                date: new Date().toISOString().split("T")[0],
                                note: data.contactNote || "",
                                person_id: task.customer.person.id,
                                product_ids: data.productIds || [],
                            })
                    }
                    if (!endService && newTaskData) {
                        createTask(newTaskData);

                        if (addToCalendar) {
                            const calendarUrl = generateOutlookCalendarLink(data, {
                                customer: task.customer?.person
                                    ? task.customer.person
                                    : undefined,
                                subjects,
                                isAllDay: isAllDay,
                                subsubjects,
                                getTranslation: t,
                            });
                            window.open(calendarUrl, "_blank");
                        }
                    } else {
                        toast.success(t("tasks.alerts.finalizeSuccess"));
                        onClose();
                    }
                },
            },
            );
    };

    const enabledBtn =
        endService ||
        (watch(["name", "subject"]).every(Boolean))

    useEffect(() => {
        if (!selectedSubject) {
            return;
        }
        
        // const currentName = watch("name");
        // if (currentName && currentName.trim() !== "") {
        //     return;
        // }
        
        const selectedSubjectObj = subjects?.find((s) => s.value === selectedSubject);
        const selectedSubsubjectObj = subsubjects?.find((ss) => ss.value === watch("subsubject"));

        if (selectedSubjectObj) {
            let generatedName = `${selectedSubjectObj.label}`
            if (selectedSubsubjectObj) {
                generatedName += ` - ${selectedSubsubjectObj.label}`
            };
            setValue("name", generatedName);
            setIsNameValueSet(true);
        }
    }, [watch("subsubject"), selectedSubject, setValue, subjects, subsubjects, watch]);


    // Helper functions for ProductSelector
    const getFormProductIds = () => watch("productIds") || [];
    const setFormProductIds = (ids) => setValue("productIds", ids);

    const isCustomerCompany = task.customer?.company
    const customerName = isCustomerCompany ? task.customer.company.name_short : `${task.customer?.person?.name || ""} ${task.customer?.person?.surname || ""}`
    const customerEmail = isCustomerCompany ? task.customer?.company?.email?.email_address : task.customer?.person?.email?.email_address
    const customerPhone = isCustomerCompany ? formatPhoneNumer(task.customer?.company?.phone?.phone_number) : formatPhoneNumer(task.customer?.person?.phone?.phone_number)

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    
    const handleEditIconClick = (event) => {
        event.stopPropagation(); // Prevent the event click from firing
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
    };

    const handleEditTask = (updatedTask) => {
        setTaskFormData(prev=> ({...prev, ...getFieldsFromObject(updatedTask, CURRENT_TASK_FIELDS_TO_EDIT)}))
    }

    const currentTaskWay = watch('way')
    const currentTaskStart = watch('startTime')
    useEffect(() => {
        if (currentTaskWay && currentTaskStart) {
            console.log(currentTaskStart, "currentTaskStart")
            setValue("stopTime", getDefaultTaskPeriodStr(dayjs(`2000-01-01T${currentTaskStart}`).toDate(), watch('way')).end)
        }
    }, [currentTaskWay, setValue]);


    return (
        <BasicDialog
            open={true}
            onClose={onClose}
            titleAlign="center"
            contentAlign="center"
            title={t("tasks.finalizeDialog.title")}
            maxWidth="xxl"
            showDialogActions={false}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    {/* Left Side - Current Task Info */}
                    <Grid item xs={7}>
                        <div style={{display:"flex", justifyContent:"center"}}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t("tasks.finalizeDialog.currentTask")}
                            </Typography>
                            <EditIcon size="small" style={{cursor: "pointer", marginLeft: 5, marginTop: 2}} color="primary" onClick={handleEditIconClick}/>
                        </div>
                        <Stack spacing={2}>
                            <TextField
                                label={t("tasks.addTaskDialog.name")}
                                value={taskFormData?.name}
                                disabled
                                fullWidth
                            />
                            {/* <TextField
                                label={t("tasks.addTaskDialog.subject")}
                                value={t(`tasks.subjects.${taskFormData?.subject.name_eng}`)}
                                disabled
                                fullWidth
                            /> */}
                            <TextField
                                label={t("tasks.addTaskDialog.way")}
                                value={t(`tasks.ways.${taskFormData?.way}`)}
                                disabled
                                fullWidth
                            />
                            {/* <TextField
                                label={t("tasks.addTaskDialog.priority")}
                                value={t(`tasks.priorities.${taskFormData?.priority}`)}
                                disabled
                                fullWidth
                            /> */}
                            <TextField
                                label={t("tasks.addTaskDialog.note")}
                                value={taskFormData?.note}
                                disabled
                                multiline
                                // rows={3}
                                fullWidth
                                inputProps={{style:{ resize: "vertical" }}}
                            />
                        </Stack>
                        {task.customer && (
                            <Grid container columns={12} direction={"row"} columnSpacing={1} marginTop={2} alignItems={"center"}>
                                <Grid item xs={0.6}>
                                    <ClientPassportButton clientId={task.customer.id} clientType={isCustomerCompany ? "company": "customer"}/>
                                </Grid>
                                <Grid item xs={3.4}>
                                    <TextField
                                        // label={t("tasks.addTaskDialog.customer")}
                                        value={customerName}
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <OutlinedInput
                                        endAdornment={
                                                <InputAdornment position="end">
                                                <CopyToClipboardButton onCopy={()=>navigator.clipboard.writeText(customerEmail)}/>   
                                                </InputAdornment>
                                            }
                                        label={t("tasks.addTaskDialog.email")}
                                        value={customerEmail}
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <OutlinedInput
                                        endAdornment={
                                                <InputAdornment position="end">
                                                <CopyToClipboardButton onCopy={()=>navigator.clipboard.writeText(customerPhone)}/>   
                                                </InputAdornment>
                                            }
                                        label={t("tasks.addTaskDialog.phone")}
                                        value={customerPhone}
                                        disabled
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider orientation="horizontal" flexItem sx={{ mt: 3 }} />
                                    <LeadsContactsOffersAgrementsTabs personId={task.customer.person?.id || task.customer.id} customerId={task.customer.id}/>
                                </Grid>
                            </ Grid>
                        )}

                        {/* Customer Completion Section */}
                        {task.customer && (
                            <Stack spacing={2} sx={{ mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={wasCompleted}
                                            onChange={(e) => setWasCompleted(e.target.checked)}
                                        />
                                    }
                                    label={t("tasks.finalizeDialog.wasCompleted")}
                                />

                                {wasCompleted && (
                                    <>
                                        <ControlledTextField
                                            control={control}
                                            name="contactNote"
                                            label={`${t("tasks.finalizeDialog.contactNote")}*`}
                                            props={{
                                                multiline: true,
                                                rows: 3,
                                                error: !!errors.contactNote,
                                                helperText:
                                                    typeof errors.contactNote?.message === "string"
                                                        ? errors.contactNote.message
                                                        : undefined,
                                            }}
                                        />

                                        {/* Product selection for contact */}
                                        <Box sx={{ mt: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: "medium", mb: 1 }}
                                            >
                                                {t("contacts.dialog.relatedProducts")}
                                            </Typography>
                                            <ProductSelector
                                                getFormProductIds={getFormProductIds}
                                                setFormProductIds={setFormProductIds}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        )}
                    </Grid>

                    {/* Right Side - New Task Creation */}
                    <Grid item xs={5}>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                {t("tasks.finalizeDialog.newTask")}
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={endService}
                                        onChange={(e) => setEndService(e.target.checked)}
                                    />
                                }
                                label={t("tasks.finalizeDialog.endService")}
                            />

                            {/* Task Name */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={12}>
                                    <ControlledTextField
                                        control={control}
                                        name="name"
                                        label={`${t("tasks.addTaskDialog.name")}*`}
                                        props={{
                                            disabled: endService,
                                            error: !!errors.name,
                                            helperText:
                                                typeof errors.name?.message === "string"
                                                    ? errors.name.message
                                                    : undefined,
                                            InputLabelProps: {
                                                shrink: isNameValueSet || !!watch("name"),
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Subject and Subsubject */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={6}>
                                    <ControlledSelect
                                        control={control}
                                        name="subject"
                                        label={`${t("tasks.addTaskDialog.subject")}*`}
                                        data={subjects || []}
                                        disabled={endService}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <ControlledSelect
                                        control={control}
                                        name="subsubject"
                                        label={t("tasks.addTaskDialog.subsubject")}
                                        data={subsubjects || []}
                                        disabled={
                                            endService ||
                                            !selectedSubject ||
                                            subsubjects?.length === 0
                                        }
                                    />
                                </Grid>
                            </Grid>

                            {/* All Day / TODO Checkboxes */}
                            <AllDayTodoSwitches
                                isAllDay={isAllDay}
                                setIsAllDay={setIsAllDay}
                                isTodo={isTodo}
                                setIsTodo={setIsTodo}
                                setValue={setValue}
                                disabled={endService}
                                t={t}
                            />

                            {/* Date and Times */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={4}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale="pl"
                                    >
                                        <Controller
                                            control={control}
                                            name="date"
                                            render={({ field }) => (
                                                <DatePicker
                                                    label={`${t("tasks.addTaskDialog.date")}`}
                                                    value={field.value ? dayjs(field.value) : null}
                                                    onChange={(date) => {
                                                        field.onChange(
                                                            date ? date.format("YYYY-MM-DD") : "",
                                                        );
                                                    }}
                                                    disabled={endService || isTodo}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            error={!!errors.date}
                                                            helperText={
                                                                typeof errors.date?.message ===
                                                                "string"
                                                                    ? errors.date.message
                                                                    : undefined
                                                            }
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={4}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale="pl"
                                    >
                                        <Controller
                                            control={control}
                                            name="startTime"
                                            render={({ field }) => (
                                                <TimePicker
                                                    label={t("tasks.addTaskDialog.startTime")}
                                                    value={
                                                        field.value
                                                            ? dayjs(`2000-01-01T${field.value}`)
                                                            : null
                                                    }
                                                    onChange={(time) => {
                                                        const timeString =
                                                            time?.format("HH:mm") || "";
                                                        field.onChange(timeString);

                                                    setValue("stopTime", getDefaultTaskPeriodStr(time.toDate(), watch('way')).end);
                        
                                                    }}
                                                    disabled={endService || isAllDay || isTodo}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            helperText={t(
                                                                "tasks.addTaskDialog.timeOptional",
                                                            )}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={4}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale="pl"
                                    >
                                        <Controller
                                            control={control}
                                            name="stopTime"
                                            render={({ field }) => (
                                                <TimePicker
                                                    label={t("tasks.addTaskDialog.stopTime")}
                                                    value={
                                                        field.value
                                                            ? dayjs(`2000-01-01T${field.value}`)
                                                            : null
                                                    }
                                                    onChange={(time) => {
                                                        const timeString =
                                                            time?.format("HH:mm") || "";
                                                        field.onChange(timeString);
                                                    }}
                                                    minTime={
                                                        watch("startTime")
                                                            ? dayjs(
                                                                  `2000-01-01T${watch("startTime")}`,
                                                              )
                                                            : undefined
                                                    }
                                                    disabled={endService || isAllDay || isTodo}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            error={!!errors.stopTime}
                                                            helperText={
                                                                typeof errors.stopTime?.message ===
                                                                "string"
                                                                    ? errors.stopTime.message
                                                                    : t(
                                                                          "tasks.addTaskDialog.timeOptional",
                                                                      )
                                                            }
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>

                            {/* Status, Way, Priority */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={4}>
                                    <ControlledSelect
                                        control={control}
                                        name="status"
                                        label={t("tasks.addTaskDialog.status")}
                                        data={statuses || []}
                                        disabled={endService}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <ControlledSelect
                                        control={control}
                                        name="way"
                                        label={`${t("tasks.addTaskDialog.way")}*`}
                                        data={[
                                            { value: "PHONE", label: t("tasks.ways.PHONE") },
                                            { value: "EMAIL", label: t("tasks.ways.EMAIL") },
                                            { value: "MEETING", label: t("tasks.ways.MEETING") },
                                        ]}
                                        disabled={endService}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <ControlledSelect
                                        control={control}
                                        name="priority"
                                        label={`${t("tasks.addTaskDialog.priority")}*`}
                                        data={[
                                            { value: "LOW", label: t("tasks.priorities.LOW") },
                                            {
                                                value: "NORMAL",
                                                label: t("tasks.priorities.NORMAL"),
                                            },
                                            { value: "HIGH", label: t("tasks.priorities.HIGH") },
                                        ]}
                                        disabled={endService}
                                    />
                                </Grid>
                            </Grid>

                            {/* Seller */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={12}>
                                    <ControlledSelect
                                        control={control}
                                        name="seller"
                                        label={`${t("tasks.addTaskDialog.seller")}*`}
                                        data={sellers || []}
                                        disabled={endService}
                                    />
                                </Grid>
                            </Grid>

                            {/* Customer (disabled) */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("tasks.addTaskDialog.customer")}
                                        value={
                                            task.customer
                                                ? task.customer?.company
                                                    ? task.customer.company.name_short
                                                    : `${task.customer.person?.name || ""} ${
                                                          task.customer.person?.surname || ""
                                                      }`
                                                : ""
                                        }
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>

                            {/* Note */}
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1, pr: 2 }}>
                                <Grid item xs={12}>
                                    <ControlledTextField
                                        control={control}
                                        name="note"
                                        label={`${t("tasks.addTaskDialog.note")}`}
                                        props={{
                                            disabled: endService,
                                            InputProps: {
                                                rows: 3,
                                                multiline: true,
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Outlook Calendar Checkbox */}
                            {!endService && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Checkbox
                                        checked={addToCalendar}
                                        onChange={(e) => setAddToCalendar(e.target.checked)}
                                    />
                                    <CalendarTodayIcon
                                        color={addToCalendar ? "primary" : "action"}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button variant="outlined" onClick={onClose}>
                                {t("tasks.finalizeDialog.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!endService && !enabledBtn}
                            >
                                {t("tasks.finalizeDialog.finalize")}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
            {isEditDialogOpen && (
                <EditTaskDialog task={taskFormData} onClose={handleCloseEditDialog} handleEditTask={handleEditTask}/>
            )}
        </BasicDialog>
    );
}
